import Backbone from 'backbone'
import _ from 'lodash'
import CalendarControl from 'scoreboard/components/calendar-control'
import TimeControl from './time-control'
import { DATE_FORMAT, getDateTimeString, splitFieldsObjectToDateTime } from 'scoreboard/components/date-time-util'

/**
 * A TimeControl, optionally joined with a CalendarControl which is used to pick
 * times or date/times.
 */
const DateTimeControl = Backbone.View.extend({

	className: 'date-time-control',

	/**
	 * @param {Object} [options.dateTime.time] A time value which is to be loaded into the
	 * TimeControl upon creation.
	 * @param {Object} [options.dateTime.date] A date value which is to be loaded into the
	 * CalendarControl upon creation.
	 * @param {Boolean} [options.hasCalendarControl] True if this control features a
	 * calendar control, else false.
	 * @param {String} [options.timeDisplayType] how to display the time
	 * @param {Boolean} [options.singularLabels=true] Whether or not the labels used should be plural (ex: hrs vs. hr)
	 */
	initialize(options = {}) {
		this.options = options
		this.time = _.result(this.options.dateTime, 'time')
		this.date = _.result(this.options.dateTime, 'date')
		this.singularLabels = _.get(this.options, 'singularLabels', true)
	},

	render() {
		this.timeControl = this._createTimeControl()

		if (this.options.hasCalendarControl) {
			this.dateControl = this._createDateControl(this.timeControl)
			this.$el.append(this.dateControl.el)
			this.dateControl.render()
		}

		this.$el.append(this.timeControl.el)
		this.timeControl.render()

		return this
	},

	_createTimeControl() {
		let t = new TimeControl({
			initialValue: this.time,
			timeDisplayType: this.options.timeDisplayType,
			singularLabels: this.singularLabels
		})

		this.isIncomplete = false
		this._setDateTime(true, this.time)

		// NOTE: It is important to save the time here (this.time) because
		// once the tooltip goes away, this.timeControl is removed as well
		t.on('input:change', t => {
			this.time = t
			this.isIncomplete = t.isIncomplete
			this._setDateTime(false, this.timeControl.getTime())
		})

		this.time && t.setTime(this.time)

		return t
	},

	_createDateControl(timeControl) {
		if (!this.options.hasCalendarControl) {
			return
		}

		let cal = new CalendarControl({
			tooltip: false,
			initialValue: this.date
		})

		// NOTE: It is important to save the date here (this.date) because
		// once the tooltip goes away, this calendar is removed as well
		cal.on('change', d => {
			this.date = d.format(DATE_FORMAT)
			this._setDateTime(false, this.timeControl.getTime())
			timeControl.focus()
		})

		return cal
	},

	_setDateTime(silent, time) {
		this.time = time
		if (this.options.hasCalendarControl && this.dateControl) {
			this.date = this.dateControl.getMoment().format(DATE_FORMAT)
		}

		this._refreshStringDateTime()
		if (!silent) this.trigger('onSetDateTime', this.getDateTime())
	},

	_refreshStringDateTime: function() {
		this.stringDateTime = getDateTimeString({date: this.date, time: this.time},
			this.options.timeDisplayType,
			this.options.hasCalendarControl
		)
	},

	// #36295: Used to keep input controls in sync, when time or datetime is updated
	// externally. i.e. fixing datetime ranges
	updateDateTime: function(dt = {}) {
		this.date = dt.date
		this.time = dt.time
		this.setDate(dt.date)
		this.setTime(dt.time)
		this._refreshStringDateTime()
	},

	getDateTime() {
		return {
			string: this.stringDateTime,
			time: this.time,
			date: this.date,
			moment: splitFieldsObjectToDateTime({
				date: this.date,
				time: this.time
			}),
			isIncomplete: this.isIncomplete
		}
	},

	setTime(t) {
		if (_.isNil(t)) {
			return
		}

		this.timeControl.setTime(t)
	},

	setDate(d) {
		if (_.isNil(d)) {
			return
		}

		this.dateControl.set(d)
		this.dateControl.updateDropdownView()
	},

	focus() {
		this.timeControl.focus()
	},

	clear() {
		delete this.stringDateTime
		delete this.time
		delete this.date
		_.invoke(this.timeControl, 'clear')
	},

	remove() {
		// If this control is being removed, call out with one final onSetDateTime
		// trigger. This is particularly useful when this control resides within a tooltip
		// because this will trigger when the tooltip closes.
		if (this.date || this.time) {
			this.trigger('onSetDateTime', _.extend({}, this.getDateTime(), {
				isIncomplete: false,
				isBeingRemoved: true
			}))
		}
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.timeControl, this.dateControl], 'remove')
	}
})

export default DateTimeControl

DateTimeControl.__filename__ = 'components/date-time-control.js'
